import emailApi from "@/api/Methods/Email-API-Methods";
import EmailTemplates, { EmailTemplateGroup } from "@/helpers/Constants/EmailTemplates.js";

function deepClone(obj) {
  return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}

export const EmailTemplatesStore = {
  namespaced: true,

  state: () => ({
    list: [],
    listLoading: false,

    currentKey: null,
    editItem: null,      // working copy being edited
    originalItem: null,  // pristine copy for dirty comparison
    currentLoading: false,

    formValid: true,
    saveRequestCounter: 0,
    saving: false,
  }),

  mutations: {
    SET_LIST(state, rows) { state.list = rows || []; },
    SET_LIST_LOADING(state, val) { state.listLoading = !!val; },

    SET_CURRENT_KEY(state, key) { state.currentKey = key || null; },
    SET_EDIT_ITEM(state, dto) { state.editItem = dto || null; },
    SET_ORIGINAL_ITEM(state, dto) { state.originalItem = dto || null; },
    SET_CURRENT_LOADING(state, val) { state.currentLoading = !!val; },

    SET_FORM_VALID(state, val) { state.formValid = !!val; },
    INCREMENT_SAVE_REQUEST(state) { state.saveRequestCounter++; },
    SET_SAVING(state, val) { state.saving = !!val; },
  },

  actions: {
    async fetchList({ commit }) {
      commit("SET_LIST_LOADING", true);
      try {
        const rows = await emailApi.methods.getEmailTemplates();
        commit("SET_LIST", rows);
      } finally {
        commit("SET_LIST_LOADING", false);
      }
    },

    async fetchTemplateByKey({ commit }, key) {
      commit("SET_CURRENT_LOADING", true);
      commit("SET_CURRENT_KEY", key);
      try {
        const dto = await emailApi.methods.getEmailTemplateConfig(key);
        commit("SET_EDIT_ITEM", deepClone(dto));
        commit("SET_ORIGINAL_ITEM", deepClone(dto));
      } finally {
        commit("SET_CURRENT_LOADING", false);
      }
    },

    requestSave({ commit }) {
      commit("INCREMENT_SAVE_REQUEST");
    },

    async saveCurrent({ state, commit, dispatch }) {
      if (!state.currentKey || !state.editItem) return;
      commit("SET_SAVING", true);
      try {
        const updated = await emailApi.methods.updateEmailTemplateConfig(state.currentKey, state.editItem);
        // refresh current with persisted result
        commit("SET_EDIT_ITEM", deepClone(updated));
        commit("SET_ORIGINAL_ITEM", deepClone(updated));
        // refresh list to reflect enabled changes etc.
        dispatch("fetchList");
      } finally {
        commit("SET_SAVING", false);
      }
    },

    setFormValid({ commit }, val) {
      commit("SET_FORM_VALID", val);
    },
  },

  getters: {
    list: state => state.list,
    listLoading: state => state.listLoading,

    resolvedRows: state => {
      return (state.list || []).map(row => {
        const keyVal = row.Key || row.key;
        const meta = EmailTemplates[keyVal] || { title: keyVal, description: '', group: EmailTemplateGroup.Agent };
        return {
          key: keyVal,
          title: meta.title || keyVal,
          description: meta.description || '',
          descriptionLong: meta.descriptionLong || '',
          group: meta.group || EmailTemplateGroup.Agent,
          enabled: row.Enabled ?? row.enabled === true,
        };
      });
    },

    agentTemplates: (state, getters) => getters.resolvedRows.filter(t => t.group === EmailTemplateGroup.Agent),
    customerTemplates: (state, getters) => getters.resolvedRows.filter(t => t.group === EmailTemplateGroup.Customer),

    currentKey: state => state.currentKey,
    editItem: state => state.editItem,
    originalItem: state => state.originalItem,
    currentLoading: state => state.currentLoading,

    isDirty: state => JSON.stringify(state.editItem) !== JSON.stringify(state.originalItem),
    formValid: state => state.formValid,

    saveRequestCounter: state => state.saveRequestCounter,
    saving: state => state.saving,
  },
};
